/**
 * Presentation for ApplicationStatus, shared by the student list and the
 * company pipeline. Mirrors ApplicationStatus in app/core/constants.py.
 */

export const APPLICATION_TONES = {
  // Waiting on the company -> warning; moving along -> info; finished well or
  // badly -> success / danger. The same rule as every other status in the
  // product (see components/ui/status.js).
  SUBMITTED: 'warning',
  UNDER_REVIEW: 'warning',
  SHORTLISTED: 'info',
  INTERVIEW: 'info',
  // The ball is in the company's court again.
  DECISION_PENDING: 'warning',
  OFFERED: 'info',
  OFFER_ACCEPTED: 'success',
  HIRED: 'success',
  REJECTED: 'danger',
  WITHDRAWN: 'neutral',
};

/** What the company calls each status. */
export const APPLICATION_LABELS = {
  SUBMITTED: 'Submitted',
  UNDER_REVIEW: 'In review',
  SHORTLISTED: 'Shortlisted',
  INTERVIEW: 'Interview',
  DECISION_PENDING: 'Decision pending',
  OFFERED: 'Offered',
  OFFER_ACCEPTED: 'Offer accepted',
  HIRED: 'Hired',
  REJECTED: 'Rejected',
  WITHDRAWN: 'Withdrawn',
};

/** Softer wording for the applicant's own view. */
export const STUDENT_APPLICATION_LABELS = {
  ...APPLICATION_LABELS,
  INTERVIEW: 'Interview scheduled',
  DECISION_PENDING: 'Awaiting decision',
  OFFERED: 'Offer received',
  OFFER_ACCEPTED: 'Offer accepted',
  HIRED: 'Joined',
  REJECTED: 'Not selected',
};

/**
 * The recruitment process as a row of steps, for the progress trackers on
 * both sides. Each step lists the statuses that mean "standing on it"; a step
 * is done once the application is on a later one.
 */
export const PIPELINE_STEPS = [
  { key: 'applied', label: 'Applied', statuses: ['SUBMITTED'] },
  { key: 'review', label: 'In review', statuses: ['UNDER_REVIEW'] },
  { key: 'shortlisted', label: 'Shortlisted', statuses: ['SHORTLISTED'] },
  { key: 'interview', label: 'Interview', statuses: ['INTERVIEW'] },
  { key: 'decision', label: 'Decision', statuses: ['DECISION_PENDING'] },
  { key: 'offer', label: 'Offer', statuses: ['OFFERED'] },
  { key: 'accepted', label: 'Accepted', statuses: ['OFFER_ACCEPTED'] },
  { key: 'joined', label: 'Joined', statuses: ['HIRED'] },
];

/** Where an open status sits in `PIPELINE_STEPS`; -1 for a closed one. */
export function stepIndex(status) {
  return PIPELINE_STEPS.findIndex((step) => step.statuses.includes(status));
}

/**
 * How far a closed application got before it closed, read from its history
 * (oldest first). Used so a rejected application still shows its progress.
 */
export function furthestStep(history = []) {
  return history.reduce((furthest, entry) => Math.max(furthest, stepIndex(entry.to_status)), 0);
}
